import { useFormatPrice, setNavigationBar, setNavbar, isInApp } from '@docs/common'
import useLanguageTrans from 'common/useLanguageTrans'

import { useMount, useRequest } from 'ahooks'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'

import { pick, throttle } from 'lodash'
import marked from 'marked'
import { getContentType } from 'utils/contentDetector'
import ReceivedPop from 'pages/coupon/ReceivedPop'
import { useTranslation } from '@volcengine/i18n'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { CMSImageEntry, CouponVariant, getVariantCouponBySKUList } from 'servers/recommend'
import startExposeObserve from '../../utils/expose'
import styles from './styles.module.scss'
import ckStyle from '@docs/common/style/ck.module.scss'
import VariantItem, { isVariantEnable, PopCoupon } from './VariantItem'
import { getStoreArea } from 'common/store'
import { StoreArea } from 'common/locales'
import {
  trackClickSolutionSku,
  trackExposeSolutionSku,
  trackSolutionLinkPage,
  trackSolutionVideoPage,
} from '../../../common/track'
import Video from 'components/Video'
import NativeVideo from 'components/NativeVideo'
import { isYouTubeVideo } from '../../../utils/videoUtils'

type Props = {
  id: string
  title?: string
  banner?: CMSImageEntry
  body?: string // markdown
  subModule: string
  onScrollPercentChange: (percent: number) => void
  leaveTrack?: () => void
  token?: string
  noPadding?: boolean
  showFixedHeader?: boolean
}

const ArticleBase: React.FC<Props> = ({
  id,
  title,
  banner,
  body,
  subModule,
  token,
  leaveTrack,
  onScrollPercentChange,
  noPadding,
  showFixedHeader,
}) => {
  const [backVisible, setBackVisible] = useState(true)

  useLanguageTrans()
  const { t } = useTranslation()
  const formatPrice = useFormatPrice()

  const stopExposeObserve = useRef<Function | null>(null)

  const bodyRef = useRef<HTMLDivElement>(null)
  const bannerRef = useRef<HTMLDivElement>(null)

  const skuDomsRef = useRef<Element[]>([])
  const videoDomsRef = useRef<Element[]>([])

  const [skuAttributes, setSkuAttributes] = useState<Array<Record<string, string>>>([])
  const [videoAttributes, setVideoAttributes] = useState<Array<Record<string, string>>>([])

  const [popCoupon, setPopCoupon] = useState<PopCoupon>()

  const ScrollTimer = useRef(0)
  const [storeArea, setStoreArea] = useState<StoreArea>()

  const skuList = useMemo(() => {
    return skuAttributes.reduce((res, curr) => {
      res.push(curr['sku'].split(',').map(sku => sku.trim()))
      return res
    }, [] as string[][])
  }, [skuAttributes])

  //获取包含了当前可领取的原始劵信息的variants
  const { data: couponVariants, loading } = useRequest(
    () => getVariantCouponBySKUList(skuList.flat(), storeArea ?? 'us'),
    {
      ready: !!skuList.length && !!token,
    }
  )

  // 智能检测内容格式：如果是 HTML 直接使用，如果是 Markdown 则用 marked 解析
  const html = useMemo(() => {
    if (!body) return '';

    const contentType = getContentType(body);

    if (contentType === 'html') {
      // HTML 格式，直接使用
      return body;
    } else if (contentType === 'markdown') {
      // Markdown 格式，使用 marked 解析
      return marked(body);
    } else {
      // 未知格式，尝试用 marked 解析（向后兼容）
      // 如果 marked 解析失败或结果与原内容相同，则直接使用原内容
      try {
        const parsed = marked(body);
        return parsed !== body ? parsed : body;
      } catch (error) {
        console.warn('Failed to parse content as markdown, using raw content:', error);
        return body;
      }
    }
  }, [body])

  const galleryId = useMemo(() => `gallery_${id}`, [id])

  const skuVariantMap = useMemo(() => {
    return couponVariants
      ?.filter(variant => isVariantEnable(variant))
      .reduce((res, curr) => {
        res[curr.sku] = curr
        return res
      }, {} as Record<string, CouponVariant>)
  }, [couponVariants])

  const displayVariants = useMemo(() => {
    return skuList.map(skus => {
      const _sortSkus = skus
        .filter(sku => isVariantEnable(skuVariantMap?.[sku]!))
        .sort((sku1, sku2) => {
          if (
            (skuVariantMap?.[sku1].inventoryQuantity ?? 0) > 0 &&
            (skuVariantMap?.[sku2].inventoryQuantity ?? 0) <= 0
          ) {
            return -1
          }
          return 0
        })
      return skuVariantMap?.[_sortSkus?.[0] || skus[0]]
    })
  }, [skuList, skuVariantMap])

  const hasBanner = useMemo(() => !!banner?.attributes?.url, [banner])

  const shouldShowFixedHeader = showFixedHeader || !hasBanner
  console.log('ArticleBase render:', { id, title, banner, body, subModule, noPadding, showFixedHeader, hasBanner })

  const onBack = () => {
    leaveTrack?.()
    window.location.href = 'app://back'
  }
  //点击sku埋点
  const variantClickTrack = useCallback(
    (variant: CouponVariant) => {
      leaveTrack?.()
      trackClickSolutionSku(id, 'clickSku', variant.sku)
    },
    [id, leaveTrack]
  )

  //埋点
  const handleProductExpose = useCallback(
    (sku: string) => {
      trackExposeSolutionSku(id, sku)
    },
    [id]
  )

  useEffect(() => {
    setNavigationBar({
      hidden: true,
    })
    // 隐藏原生导航条
    setTimeout(() => {
      setNavbar({
        hideNavbar: true, // 隐藏 App 的导航条, 使用自定义的
        backOperation: 1,
      })
    }, 500)
  })

  // 页面加载完成后，渲染sku
  useEffect(() => {
    if (html) {
      setTimeout(() => {
        const skuDoms = bodyRef.current?.querySelectorAll('[role=sku]') || []
        const videoDoms = bodyRef.current?.querySelectorAll('[role=video]') || []

        skuDomsRef.current = Array.from(skuDoms!)
        videoDomsRef.current = Array.from(videoDoms!)
        const skuList = Array.from(skuDoms || []).map(dom => {
          const attributes = dom.getAttributeNames().reduce((res, curr) => {
            res[curr] = dom.getAttribute(curr)!
            return res
          }, {} as Record<string, string>)
          return pick(attributes, ['sku'])
        })
        const videoAttributes = Array.from(videoDoms || []).map(dom => {
          const attributes = dom.getAttributeNames().reduce((res, curr) => {
            res[curr] = dom.getAttribute(curr)!
            return res
          }, {} as Record<string, string>)
          return pick(attributes, ['url'])
        })

        setSkuAttributes(skuList)

        setVideoAttributes(videoAttributes)
      }, 0)
    }
  }, [html, couponVariants])
  // sku
  useEffect(() => {
    skuDomsRef.current?.forEach((skuDom, i) => {
      const _Element = React.createElement(() => (
        <VariantItem
          onVariantClickTrack={variantClickTrack}
          loading={loading}
          recommendTrans={t}
          variant={displayVariants?.[i]}
          onGetCouponSucc={setPopCoupon}
          formatPrice={formatPrice}
        />
      ))
      ReactDOM.render(_Element, skuDom)
    })
  }, [skuList, couponVariants, t, loading, variantClickTrack, displayVariants, formatPrice])

  // video
  useEffect(() => {
    videoDomsRef.current?.forEach((dom, i) => {
      const VideoWrapper = ({
        videoAttributes,
        id,
        onVideoClick,
      }: {
        videoAttributes: any
        id: string
        onVideoClick: () => void
      }) => {
        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation()
          onVideoClick() // 调用传入的点击处理函数
        }

        // 判断是否为YouTube视频
        const isYouTube = isYouTubeVideo(videoAttributes.url)
        return (
          <div className={styles.videoWrap} onClick={e => handleClick(e)}>
            {isYouTube ? (
              <Video
                key={videoAttributes.id}
                pn=""
                value={videoAttributes.url}
                {...videoAttributes}
                uid={id}
                sub_module="solution"
                art_id={+id}
              />
            ) : (
              <NativeVideo
                key={videoAttributes.id}
                pn=""
                value={videoAttributes.url}
                {...videoAttributes}
                uid={id}
                sub_module="solution"
                art_id={+id}
              />
            )}
          </div>
        )
      }
      const handleVideoClick = () => {
        // 调用埋点函数
        trackSolutionVideoPage(id, videoAttributes[i].url)
      }

      const _Element = React.createElement(VideoWrapper, {
        videoAttributes: videoAttributes[i],
        id: id,
        onVideoClick: handleVideoClick,
      })
      ReactDOM.render(_Element, dom)
    })
  }, [id, videoAttributes])

  useEffect(() => {
    setTimeout(() => {
      stopExposeObserve.current && stopExposeObserve.current()
      stopExposeObserve.current = startExposeObserve(null, '.variant-item', handleProductExpose)
    }, 500)
  }, [handleProductExpose, skuList])

  useMount(() => {
    document.addEventListener(
      'scroll',
      throttle(() => {
        // 页面总高
        var totalH = document.body.scrollHeight || document.documentElement.scrollHeight
        // 可视高
        var clientH = window.innerHeight || document.documentElement.clientHeight
        // 计算有效高
        var validH = totalH - clientH
        // 滚动条卷去高度
        var scrollH = document.body.scrollTop || document.documentElement.scrollTop
        // 百分比
        var result = Math.ceil((scrollH / validH) * 100)
        onScrollPercentChange(result > 100 ? 100 : result < 0 ? 0 : result)

        setBackVisible(false)
        if (ScrollTimer.current !== null) {
          clearTimeout(ScrollTimer.current)
        }
        ScrollTimer.current = window.setTimeout(() => {
          setBackVisible(true)
        }, 500)
      }, 300)
    )
  })

  useEffect(() => {
    if (!html) return
    const imgs = bodyRef.current?.querySelectorAll('img')
    if (!imgs?.length) return
    imgs.forEach((img, i) => {
      if (img.parentElement?.tagName?.toLowerCase() !== 'a') {
        const { naturalWidth: w, naturalHeight: h, src } = img
        const aEle = document.createElement('a')
        aEle.setAttribute('class', 'view-wraper')
        aEle.setAttribute('key', `${galleryId}_${i}`)
        aEle.setAttribute('target', '_blank')
        aEle.setAttribute('rel', 'noreferrer')
        aEle.setAttribute('href', src)
        aEle.setAttribute('data-pswp-width', String(w || 375))
        aEle.setAttribute('data-pswp-height', String(h || 375))
        img.parentNode?.insertBefore(aEle, img.nextElementSibling)
        aEle.appendChild(img)
        img.onload = e => {
          const { naturalWidth: w, naturalHeight: h } = e.target as HTMLImageElement
          aEle.setAttribute('data-pswp-width', String(w))
          aEle.setAttribute('data-pswp-height', String(h))
        }
      }
    })

    const lightbox = new PhotoSwipeLightbox({
      gallery: `#${galleryId}`,
      children: 'a.view-wraper',
      initialZoomLevel: 'fit',
      pswpModule: () => import('photoswipe'),
    })
    lightbox.init()

    return () => {
      lightbox.destroy()
    }
  }, [html, galleryId])

  useEffect(() => {
    if (html) {
      const aElements = bodyRef.current?.querySelectorAll('a:not(.view-wraper)')
      if (!aElements?.length) return
      aElements.forEach(aElement => {
        // 为 a 元素添加点击事件监听器
        aElement.addEventListener('click', async event => {
          event.preventDefault() // 阻止默认的链接跳转行为
          const href = aElement.getAttribute('href')
          // 调用埋点函数，传入必要的参数
          href && (await trackSolutionLinkPage(id, href))
          // 执行完埋点函数后，进行页面跳转

          if (href) {
            setTimeout(() => {
              window.location.href = href
            }, 1000)
          }
        })
      })
    }
  }, [html, id])

  useEffect(() => {
    getStoreArea().then(storeArea => {
      setStoreArea(storeArea as StoreArea)
    })
  }, [])

  // 动态执行 HTML 中的 script 标签
  useEffect(() => {
    if (!html || !bodyRef.current) return

    // 提取 HTML 中的所有 script 标签内容
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html

    const scriptTags = tempDiv.querySelectorAll('script')
    const createdScripts: HTMLScriptElement[] = []

    // 延迟执行，确保 DOM 已经渲染
    setTimeout(() => {
      scriptTags.forEach(scriptTag => {
        const newScript = document.createElement('script')

        // 如果有 src 属性，复制它
        if (scriptTag.src) {
          newScript.src = scriptTag.src
        }

        // 复制 script 内容，并修复 preloadImg 变量名
        if (scriptTag.textContent) {
          newScript.textContent = scriptTag.textContent
        }

        // 复制其他属性
        Array.from(scriptTag.attributes).forEach(attr => {
          if (attr.name !== 'src') {
            newScript.setAttribute(attr.name, attr.value)
          }
        })

        // 将新的 script 插入到 body 中
        document.body.appendChild(newScript)
        createdScripts.push(newScript)
      })
    }, 100)

    // 清理函数：移除动态创建的 script 标签
    return () => {
      createdScripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script)
        }
      })
    }
  }, [html])

  const [isApp, setIsApp] = useState(false)

  useEffect(() => {
    const fun = async () => {
      const isApp = await isInApp()
      setIsApp(isApp)
    }
    fun()
  }, [])

  return (
    <>
      <div className={styles.articleWrap}>
        {shouldShowFixedHeader && (
          <div className={styles.fixedHeader}>
            {isApp && <i className={styles.headerBackIcon} onClick={onBack}></i>}
            <div className={styles.headerTitle} style={{ paddingLeft: isApp ? undefined : 0, paddingRight: isApp ? 36 : 0 }}>{title}</div>
          </div>
        )}
        {isApp && hasBanner && (
          <div className={styles.navigationBar} data-name="navigation">
            <i className={styles.backIcon} onClick={onBack} style={{ opacity: backVisible ? 0.7 : 0 }}></i>
          </div>
        )}
        {hasBanner && (
          <div className={styles.banner} ref={bannerRef}>
            <img src={banner?.attributes?.url} alt={title} />
          </div>
        )}
        <div className={styles.contentWrap} style={{
          marginTop: hasBanner ? undefined : 0,
          paddingLeft: noPadding ? 0 : undefined,
          paddingRight: noPadding ? 0 : undefined,
          paddingBottom: noPadding ? 0 : undefined,
          paddingTop: shouldShowFixedHeader ? 60 : (noPadding ? 0 : undefined)
        }}>
          {!showFixedHeader && <div className={styles.title}>{title}</div>}
          <div id={galleryId} className={ckStyle.body} ref={bodyRef} dangerouslySetInnerHTML={{ __html: html }}></div>
        </div>
      </div>
      {/* sku领取的时候弹框 */}
      <ReceivedPop popupCoupon={popCoupon} />
    </>
  )
}

export default ArticleBase
